import {Grid, makeStyles, Typography, useMediaQuery} from '@material-ui/core';
import {AgentDto, CommissionAgentDto, OwnerDto, PersonDto} from 'Agent/Policies/api';
import React, {ReactNode} from 'react';
import {AllstateCardV2} from 'Shared/components/AllstateCardV2';
import {CardFieldV2} from 'Shared/components/CardFieldV2';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {maskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';
import {SwipeableView} from '../../../../Shared/components/SwipeableView';

const usePlanBreakDownStyles = makeStyles(theme => ({
  planbreakdown: {
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '1.15rem',
    },
    '&::-webkit-scrollbar-track': {
      background: '#E5E5E5',
      borderRadius: '0.35rem',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#A6A5A5',
      border: '5px solid transparent',
      borderRadius: '8rem',
      backgroundClip: 'content-box',
    },
    margin: '0.25rem .25rem',
    [theme.breakpoints.up('md')]: {
      maxHeight: '85rem',
    },
  },
}));

const usePolicyInformationSectionStyles = makeStyles(_theme => ({
  heading: {
    color: '#0E1738',
    width: '100%',
    fontSize: '1rem',
    fontWeight: 600,
    textAlign: 'left',
    padding: '0 1.75rem',
    marginBottom: '-0.25rem',
  },
  gridContainer: {
    padding: '1rem',
    width: '100%',
    margin: 0,
    justifyContent: 'flex-start',
  },
  separator: {
    border: '1.5px solid #ccc',
    width: '95%',
    '&:last-of-type': {
      display: 'none',
    },
  },
  boxShadowStyle: {
    boxShadow: '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)',
    cursor: 'pointer',
  },
}));

type PolicyInformationSectionProps = {
  title: string;
  children?: ReactNode;
};

export const PolicyInformationSection = ({title, children}: PolicyInformationSectionProps) => {
  const {gridContainer, separator, heading} = usePolicyInformationSectionStyles();
  return (
    <>
      <div>
        <h3 className={heading}>{title}</h3>
        <Grid container spacing={3} className={gridContainer}>
          {children}
        </Grid>
      </div>
      <hr className={separator} />
    </>
  );
};

type PolicyHolderProps = {
  role: 'Insured' | 'Owner';
  policyHolder: OwnerDto | PersonDto;
};

export const PolicyHolder = (props: PolicyHolderProps) => (
  <PolicyInformationSection title={`${props.role} Information`}>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Name" value={props.policyHolder.fullName} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Date of Birth" value={toFormattedDateAsUTC(props.policyHolder.dateOfBirth)} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2
        name="SSN"
        value={props.policyHolder.identifier && `***-**-${props.policyHolder.identifier}`}
        ddprivacy="mask"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2
        name="Address"
        value={props.policyHolder.address && props.policyHolder.address?.fullAddress}
        ddprivacy="mask"
      />
    </Grid>
    {props.role === 'Owner' && 'phoneNumber' in props.policyHolder && (
      <Grid item xs={12} sm={6} md={12}>
        <CardFieldV2 name="Phone" value={maskPhoneNumber(props.policyHolder.phoneNumber)} ddprivacy="mask" />
      </Grid>
    )}
  </PolicyInformationSection>
);

type PolicyDetailProps = {
  status?: string;
  policyNumber?: string;
  issueDate?: Date;
  specialHandlingInstruction?: string;
};

export const PolicyDetail = (props: PolicyDetailProps) => (
  <PolicyInformationSection title="Policy Details">
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Status" value={props.status} />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Number" value={props.policyNumber} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Issue Date" value={toFormattedDateAsUTC(props.issueDate)} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Special Handling" value={props.specialHandlingInstruction} />
    </Grid>
  </PolicyInformationSection>
);

type AccountDetailProps = {
  accountName?: string;
  accountNumber?: string;
  previousAccountName?: string;
  previousAccountNumber?: string;
};

export const AccountDetails = (props: AccountDetailProps) => (
  <PolicyInformationSection title="Account">
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Name" value={props.accountName} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Number" value={props.accountNumber} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Previous Name" value={props.previousAccountName} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Previous Number" value={props.previousAccountNumber} ddprivacy="mask" />
    </Grid>
  </PolicyInformationSection>
);

type PolicyAgentProps = {
  agent: AgentDto;
  agentRole: string;
};

export const PolicyAgent = (props: PolicyAgentProps) => (
  <PolicyInformationSection title={props.agentRole}>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Name" value={props.agent.name} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Number" value={props.agent.number} ddprivacy="mask" />
    </Grid>
  </PolicyInformationSection>
);

type commissionAgentProps = {
  commissionAgent: CommissionAgentDto;
};

export const CommissionAgent = (props: commissionAgentProps) => (
  <PolicyInformationSection title="Commission Agent">
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Name" value={props.commissionAgent.name} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Number" value={props.commissionAgent.number} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Commission Phase" value={props.commissionAgent.commissionPhase} />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Commission Split" value={props.commissionAgent.commissionPercent} />
    </Grid>
  </PolicyInformationSection>
);

type PlanBreakdownProps = {
  children: ReactNode;
};

const PlanBreakdown = (props: PlanBreakdownProps) => {
  const classes = usePlanBreakDownStyles();
  const {boxShadowStyle} = usePolicyInformationSectionStyles();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return isMobile ? (
    <SwipeableView title="Plan Breakdown" styles={{swipe: boxShadowStyle}}>
      {props.children}
    </SwipeableView>
  ) : (
    <AllstateCardV2 title="Plan Breakdown" className={classes.planbreakdown}>
      <Typography component="div" tabIndex={0}>
        {props.children}
      </Typography>
    </AllstateCardV2>
  );
};

export default PlanBreakdown;
